// One of the federal questions, with two answers and no third state to fall
// into by accident.
//
// `value` is required and may be null. There is deliberately no default and no
// `defaultValue` prop: an unanswered certification is a missing answer, not a
// "No", and the only way to make this component show an answer is for a driver
// to have given one.

export interface ComplianceQuestionProps {
  question: string
  /** null until the driver answers. Required — this cannot be defaulted. */
  value: boolean | null
  onAnswer: (value: boolean) => void
  error?: string
}

const join = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ')

const option = (selected: boolean) =>
  join(
    'flex-1 rounded-[9px] border py-2.5 text-center text-[12px] font-semibold',
    selected
      ? 'border-doubleNickel-brand-500 bg-doubleNickel-brand-25 text-doubleNickel-brand-700'
      : 'border-doubleNickel-gray-300 bg-white text-doubleNickel-gray-700'
  )

export const ComplianceQuestion = ({
  question,
  value,
  onAnswer,
  error,
}: ComplianceQuestionProps) => {
  // Answered means an explicit boolean. Anything else — null, or a value that
  // went missing on the way here — is unanswered, so a hole in a caller can
  // never present itself as an answer the driver gave.
  const answered = value === true || value === false

  return (
    <div className="border-b border-doubleNickel-gray-100 py-2 last:border-b-0">
      <div
        className="text-[11.5px] leading-snug text-doubleNickel-gray-800"
        id={`q-${question.slice(0, 24)}`}
      >
        {question}
        <span className="ml-0.5 text-doubleNickel-error-600">*</span>
      </div>
      <div
        role="radiogroup"
        aria-label={question}
        data-answered={answered ? 'true' : 'false'}
        className="mt-1.5 flex gap-2"
      >
        <button
          type="button"
          role="radio"
          aria-checked={value === true}
          onClick={() => onAnswer(true)}
          className={option(value === true)}
        >
          Yes
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={value === false}
          onClick={() => onAnswer(false)}
          className={option(value === false)}
        >
          No
        </button>
      </div>
      {error && (
        <div className="mt-1 text-[10px] text-doubleNickel-error-700">
          {error}
        </div>
      )}
    </div>
  )
}
